import { ReactElement } from 'react';
import { HiOutlineCircleStack } from 'react-icons/hi2';
import { getChannel } from 'utils/channelNamesGridded';
import { numberToLetters } from 'utils/letterNumber';
import { formatRunWallClock, runTimestampLabel } from 'utils/sessionTime';
import { useProfile } from '../../../hooks/useProfile';

function RunSummary(props: { run: any; calibration: any; isLoading: boolean }): ReactElement {
  const { run, calibration, isLoading } = props;
  const { data: { show_blob_controls } } = useProfile();
  // Untimestamped runs still get a line — "Processing…" / "NA" — rather than
  // silently dropping the header row.
  const timeLine = run ? runTimestampLabel(run, 'datetime') : '';

  // Run duration from HealthCheck total_run_time_seconds. Format "Duration 32m 15s";
  // minutes roll past 60 rather than into hours (e.g. "122m 10s"). Round the total
  // before splitting so seconds can't round up to 60.
  const totalRunSeconds = run?.run_health_obj?.total_run_time_seconds;
  let durationText = '';
  if (typeof totalRunSeconds === 'number' && totalRunSeconds > 0) {
    const totalSecs = Math.round(totalRunSeconds);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    durationText = `Duration ${mins}m ${String(secs).padStart(2, '0')}s`;
  }

  // Calculate distance
  let distanceText = '';
  if (isLoading) {
    distanceText = 'Loading...';
  } else {
    const distances = getChannel(run?.data_gridded, 'Distance_km', calibration);
    distanceText = distances ? `${distances[distances.length - 1].toFixed(2)}km` : '';
  }

  // Traverse count from TraverseLap_NG00 channel (max value = number of traverses)
  let traverseCount: number | null = null;
  const traverseChannel = run?.data_gridded?.['TraverseLap_NG00'] as number[] | undefined;
  if (traverseChannel && traverseChannel.length > 0) {
    let max = 0;
    for (const v of traverseChannel) {
      if (v > max) max = v;
    }
    if (max > 0) traverseCount = max;
  }

  // Title line: "Config A - Baseline - Run 2"
  const configLetter = calibration?.letter;
  const configDesc = calibration?.description;
  const runNum = run?.run_number;
  const titleParts: string[] = [];
  if (configLetter) titleParts.push(`Config ${configLetter}`);
  if (configDesc) titleParts.push(configDesc);
  if (runNum != null) titleParts.push(`Run ${runNum}`);
  const titleLine = titleParts.join(' - ') || run?.description || 'Run';

  // Distance + traverses line
  const distanceParts: string[] = [];
  if (distanceText) distanceParts.push(`Distance ${distanceText}`);
  if (traverseCount != null) distanceParts.push(`${traverseCount} Traverse${traverseCount !== 1 ? 's' : ''}`);
  const distanceLine = distanceParts.join(' - ');

  return (
    <div>
      <div className="cr-text-lg cr-font-bold cr-text-blue-900">{titleLine}</div>
      {timeLine && (
        <div className="cr-flex cr-items-baseline cr-gap-4 cr-text-base cr-text-blue-900/70">
          <span>{timeLine}</span>
          {durationText && <span>{durationText}</span>}
        </div>
      )}
      {distanceLine && (
        <div className="cr-text-base cr-text-blue-900/70">
          {distanceLine}
        </div>
      )}
      {show_blob_controls && (run?.source_episode_id || run?.source_used) && (
        <div
          className="cr-mt-1 cr-flex cr-items-center cr-gap-1.5 cr-text-sm cr-text-blue-900/50"
          title={
            run?.source_episode_id
              ? `Linked to Nimbus episode ${run.source_episode_id}`
              : 'No Nimbus episode linked to this run'
          }
        >
          <HiOutlineCircleStack className="cr-h-4 cr-w-4 cr-shrink-0" />
          <span>
            {run?.source_episode_id ? `Episode ${String(run.source_episode_id).slice(0, 8)}` : 'No episode'}
            {run?.source_used === 'stream' && ' — processed from the episode stream'}
            {run?.source_used === 'device_zip' && ' — processed from the device zip'}
          </span>
        </div>
      )}
      {run?.run_health_obj?.data_source_fallback && (
        <div
          className="cr-mt-1 cr-text-sm cr-font-semibold cr-text-amber-700"
          title={run.run_health_obj.data_source_fallback}
        >
          Data source fallback — {run.run_health_obj.data_source_fallback}
        </div>
      )}
      {run?.partial && (
        <div className="cr-mt-1 cr-text-sm cr-font-semibold cr-text-amber-700">
          Retroactively terminated — end time inferred from last received data
        </div>
      )}
      {run?.history && run.history.length > 1 && (
        <div className="cr-mt-3">
          <div className="cr-text-sm cr-font-semibold cr-text-blue-900/70">
            Config History
          </div>
          {[...run.history].reverse().map(
            (entry: any, idx: number) => (
              <div
                key={idx}
                className="cr-text-sm cr-text-blue-900/50"
              >
                {entry.letter_number === 0
                  ? 'Discard'
                  : `Config ${numberToLetters(entry.letter_number)}`}
                {entry.description ? ` - ${entry.description}` : ''}
                {entry.assignment_ts
                  ? ` (${formatRunWallClock(entry.assignment_ts, run?.timezone, 'datetime')})`
                  : ''}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

export default RunSummary;
